/**
 * ReportPivotPoints - Two-part pivot point display:
 * 1. AI-discovered pivot points (day 1) - D3 force graph + table
 * 2. Human-created pivot points (day 2) - participant teams' own pivot points with blockers/possibilities
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { SCENARIO_COLORS_BY_NAME } from '../../config/brandColors';
import WorkshopAction from './WorkshopAction';
import {
  Section, SectionTitle, SectionSubtitle, Paragraph,
  GraphContainer, GraphLegend, LegendItem, LegendDot, SITRA,
} from './reportStyles';
import EditableText from './EditableText';
import TranslatedText from './TranslatedText';
import ReportImageSlot from './ReportImageSlot';
import { useReportEdits } from './ReportEditsContext';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const PivotGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const PivotCard = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: 4px solid ${p => p.$color || '#6b7280'};
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  @media print { page-break-inside: avoid; box-shadow: none; }
`;

const PivotHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  &:hover { background: #fafafa; }
  @media print { &:hover { background: transparent; } }
`;

const PivotTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  color: #000;
  margin: 0;
  /* flex:1 means basis 0, and the meta badges refuse to shrink — with 4
     scenario names + a connection count the title was squeezed to one word per
     line. Give it a real basis and a floor so the question stays readable. */
  flex: 1 1 320px;
  min-width: 240px;
`;

const PivotMeta = styled.div`
  display: flex;
  align-items: center;
  /* Badges wrap onto their own line rather than starving the title. */
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
`;

const MetaBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 0;
  background: ${p => p.$bg || '#f5f5f5'};
  color: ${p => p.$color || '#367ABA'};
  flex-shrink: 0;
`;

const ToggleIcon = styled.span`
  color: #9ca3af;
  flex-shrink: 0;
  @media print { display: none; }
`;

const PivotBody = styled.div`
  padding: 0 20px 16px;
  border-top: 1px solid #f3f4f6;
`;

const PivotDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  padding: 8px 20px 0;
`;

const ScenarioPill = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 0;
  background: ${p => p.$color || '#6b7280'}22;
  color: ${p => p.$color || '#6b7280'};
`;

const ItemsSection = styled.div`
  margin-top: 12px;
`;

const ItemsSectionTitle = styled.h5`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.$color || '#333'};
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PivotItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  color: #444;
  border-bottom: 1px solid #f9fafb;
  &:last-child { border-bottom: none; }
`;

const ItemDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
  background: ${p => p.$type === 'obstacle' ? SITRA.puolukka : SITRA.havu};
`;

const SourceTag = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 4px;
  margin-left: 8px;
  vertical-align: middle;
  color: ${p => p.$manual ? '#7a3a0c' : '#7c3aed'};
  background: ${p => p.$manual ? 'rgba(255, 217, 61, 0.25)' : '#ede9fe'};
`;

function getScenarioColor(name) {
  const lower = name?.toLowerCase() || '';
  for (const [key, color] of Object.entries(SCENARIO_COLORS_BY_NAME)) {
    if (lower.includes(key.toLowerCase())) return color;
  }
  return '#6b7280';
}

// D3 force graph for AI pivot points
function PivotPointGraph({ pivotPoints, width = 800, height = 600 }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!pivotPoints?.length || !svgRef.current) return;

    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height);

    const defs = svg.append('defs');

    const nodes = pivotPoints.slice(0, 15).map((pivot, i) => {
      const scenarioNames = pivot.scenarioNames || [];
      const colors = scenarioNames.map(name => getScenarioColor(name));
      const uniqueColors = [...new Set(colors)];
      const gradientId = `pivot-gradient-${i}`;

      if (uniqueColors.length > 1) {
        const gradient = defs.append('linearGradient')
          .attr('id', gradientId)
          .attr('x1', '0%').attr('y1', '0%')
          .attr('x2', '100%').attr('y2', '100%');
        uniqueColors.forEach((color, idx) => {
          gradient.append('stop')
            .attr('offset', `${(idx / (uniqueColors.length - 1)) * 100}%`)
            .attr('stop-color', color);
        });
      }

      return {
        id: pivot.id || i,
        text: pivot.displayTitle || pivot.text || pivot.title || `Vipupiste ${i + 1}`,
        votes: pivot.votes || pivot.memberCount || pivot.size || 5,
        scenarioNames,
        fill: uniqueColors.length > 1 ? `url(#${gradientId})` : (uniqueColors[0] || '#6b7280'),
      };
    });

    const links = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const shared = nodes[i].scenarioNames.filter(s =>
          nodes[j].scenarioNames.some(s2 => s.toLowerCase() === s2.toLowerCase())
        );
        if (shared.length > 0) {
          links.push({ source: nodes[i].id, target: nodes[j].id, strength: shared.length });
        }
      }
    }

    const maxVotes = Math.max(...nodes.map(n => n.votes), 1);
    const radiusScale = d3.scaleSqrt().domain([1, maxVotes]).range([20, 50]);

    const simulation = d3.forceSimulation(nodes)
      .force('link', d3.forceLink(links).id(d => d.id).distance(120).strength(0.3))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => radiusScale(d.votes) + 10));

    const link = svg.append('g')
      .selectAll('line').data(links).join('line')
      .attr('stroke', '#e5e7eb')
      .attr('stroke-width', d => d.strength * 2)
      .attr('stroke-opacity', 0.6);

    const node = svg.append('g')
      .selectAll('g').data(nodes).join('g')
      .attr('cursor', 'default');

    node.append('circle')
      .attr('r', d => radiusScale(d.votes))
      .attr('fill', d => d.fill)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2);

    node.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', '#fff')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .text(d => d.votes);

    const labelWidth = 120;
    node.append('foreignObject')
      .attr('x', -labelWidth / 2)
      .attr('y', d => radiusScale(d.votes) + 8)
      .attr('width', labelWidth)
      .attr('height', 60)
      .append('xhtml:div')
      .style('font-size', '11px')
      .style('font-weight', '500')
      .style('color', '#000')
      .style('text-align', 'center')
      .style('line-height', '1.3')
      .style('word-wrap', 'break-word')
      .style('overflow', 'hidden')
      .text(d => d.text);

    simulation.stop();
    for (let i = 0; i < 300; i++) simulation.tick();

    nodes.forEach(d => {
      const r = radiusScale(d.votes);
      d.x = Math.max(r + 70, Math.min(width - r - 70, d.x));
      d.y = Math.max(r + 20, Math.min(height - r - 80, d.y));
    });

    link
      .attr('x1', d => d.source.x).attr('y1', d => d.source.y)
      .attr('x2', d => d.target.x).attr('y2', d => d.target.y);
    node.attr('transform', d => `translate(${d.x},${d.y})`);

  }, [pivotPoints, width, height]);

  if (!pivotPoints?.length) return null;

  const allScenarios = [...new Set(pivotPoints.flatMap(p => p.scenarioNames || []))];
  const hasMultiScenario = pivotPoints.some(p => (p.scenarioNames || []).length > 1);

  return (
    <GraphContainer>
      <svg ref={svgRef} />
      <GraphLegend>
        {allScenarios.map(name => (
          <LegendItem key={name}>
            <LegendDot $color={getScenarioColor(name)} />
            <span>{name}</span>
          </LegendItem>
        ))}
        {hasMultiScenario && (
          <LegendItem>
            <LegendDot style={{ background: '#000' }} />
            <span>Moniskenaario</span>
          </LegendItem>
        )}
      </GraphLegend>
    </GraphContainer>
  );
}

export default function ReportPivotPoints({ pivotPoints, pivotEdits, lensItems, pivotSummaries = [], selectedPivotIds = [], scenarios = [] }) {
  const { t } = useTranslation();
  const { get } = useReportEdits();
  const scenarioTitleByName = useMemo(() => {
    const map = new Map();
    for (const s of scenarios || []) {
      if (!s?.title) continue;
      map.set(String(s.title).trim().toLowerCase(), s);
    }
    return map;
  }, [scenarios]);
  const translateScenarioName = (name) => {
    const s = scenarioTitleByName.get(String(name || '').trim().toLowerCase());
    if (!s?.id) return name;
    return get(`scenario.${s.id}.title`, name);
  };
  const summariesByPivot = {};
  (pivotSummaries || []).forEach(s => {
    if (s.pivotPointId) summariesByPivot[s.pivotPointId] = s;
  });
  const [expandedPivot, setExpandedPivot] = useState(null);
  const [showAllPivots, setShowAllPivots] = useState(false);

  if (!pivotPoints?.length) return null;

  // Build edits lookup
  const editsLookup = {};
  (pivotEdits || []).forEach(e => {
    editsLookup[e.pivot_point_id] = e;
  });

  // Build lens items by pivot
  const itemsByPivot = {};
  (lensItems || []).forEach(item => {
    if (!item.pivot_point_id) return;
    if (!itemsByPivot[item.pivot_point_id]) itemsByPivot[item.pivot_point_id] = [];
    itemsByPivot[item.pivot_point_id].push(item);
  });

  // Split into AI-discovered vs human-created
  const aiPivots = pivotPoints
    .filter(p => p.source !== 'manual')
    .map(p => {
      const edit = editsLookup[p.id];
      const baseTitle = edit?.title || p.text || p.title;
      const baseDesc = edit?.description || p.description;
      return {
        ...p,
        displayTitle: p.id ? get(`pivot.${p.id}.title`, baseTitle) : baseTitle,
        displayDescription: p.id ? get(`pivot.${p.id}.description`, baseDesc) : baseDesc,
      };
    });

  const humanPivots = pivotPoints
    .filter(p => p.source === 'manual')
    .map(p => {
      const edit = editsLookup[p.id];
      const baseTitle = edit?.title || p.text || p.title;
      const baseDesc = edit?.description || p.description;
      return {
        ...p,
        displayTitle: p.id ? get(`pivot.${p.id}.title`, baseTitle) : baseTitle,
        displayDescription: p.id ? get(`pivot.${p.id}.description`, baseDesc) : baseDesc,
      };
    });

  return (
    <Section id="pivotpoints">
      <SectionTitle>{t('report.pivots.title')}</SectionTitle>

      {/* A screenshot of the pivot points belongs with them, not in the
          impact-map slot two sections up. Renders nothing when unset. */}
      <ReportImageSlot
        fieldKeyPrefix="pivots.map"
        label={t('report.pivots.mapLabel')}
        aspect="16 / 9"
        hint={t('report.pivots.mapHint')}
      />

      {/* --- AI-discovered pivot points (day 1) --- */}
      {aiPivots.length > 0 && (
        <>
          <SectionSubtitle>
            {t('report.pivots.aiHeading')}
            <SourceTag>{t('report.pivots.aiBadge')}</SourceTag>
          </SectionSubtitle>
          {(() => {
            const selectedSet = new Set(selectedPivotIds);
            const hasSelection = selectedPivotIds.length > 0 && aiPivots.some(p => selectedSet.has(p.id));
            const selectedPivots = hasSelection ? aiPivots.filter(p => selectedSet.has(p.id)) : [];
            const otherPivots = hasSelection ? aiPivots.filter(p => !selectedSet.has(p.id)) : aiPivots;

            return (<>
              {hasSelection ? (
                <WorkshopAction>
                  <EditableText
                    block
                    multiline={false}
                    fieldKey="pivot.selection_summary"
                    label="Vipupisteiden valinta"
                    vars={{ aiCount: aiPivots.length, selectedCount: selectedPivots.length }}
                    fallback={t('report.pivots.introSelected')}
                  />
                </WorkshopAction>
              ) : (
                <WorkshopAction>
                  <EditableText
                    block
                    multiline={false}
                    fieldKey="pivot.intro_summary"
                    label="Vipupisteiden johdanto"
                    vars={{ aiCount: aiPivots.length }}
                    fallback={t('report.pivots.introIdentified')}
                  />
                </WorkshopAction>
              )}

              <PivotGrid>
                {(hasSelection ? selectedPivots : aiPivots.slice(0, 15)).map((pivot, i) => {
              const pivotItems = itemsByPivot[pivot.id] || [];
              const obstacles = pivotItems.filter(it => it.type === 'obstacle');
              const opportunities = pivotItems.filter(it => it.type !== 'obstacle');
              const isExpanded = expandedPivot === pivot.id;
              const scenarioNames = pivot.scenarioNames || [];
              const primaryColor = getScenarioColor(scenarioNames[0]);

              const hasDetail = pivot.displayDescription || pivotItems.length > 0;

              return (
                <PivotCard key={pivot.id || i} $color={primaryColor}>
                  <PivotHeader onClick={() => hasDetail && setExpandedPivot(isExpanded ? null : pivot.id)}>
                    <PivotTitle>
                      {pivot.id
                        ? <EditableText fieldKey={`pivot.${pivot.id}.title`} fallback={pivot.displayTitle} multiline={false} label="Vipupisteen otsikko" />
                        : pivot.displayTitle}
                    </PivotTitle>
                    <PivotMeta>
                      {scenarioNames.length > 0 && scenarioNames.map(name => (
                        <ScenarioPill key={name} $color={getScenarioColor(name)}>
                          {translateScenarioName(name)}
                        </ScenarioPill>
                      ))}
                      {(pivot.memberCount || pivot.size) && (
                        <MetaBadge>{t('report.pivots.connections', { n: pivot.memberCount || pivot.size })}</MetaBadge>
                      )}
                      {pivotItems.length > 0 && (
                        <MetaBadge $bg="#f0fdf4" $color="#00613d">
                          {t('report.pivots.itemsShort', { count: pivotItems.length })}
                        </MetaBadge>
                      )}
                      {hasDetail && (
                        <ToggleIcon>
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </ToggleIcon>
                      )}
                    </PivotMeta>
                  </PivotHeader>

                  {isExpanded && (
                    <PivotBody>
                      {pivot.displayDescription && (
                        <PivotDescription style={{ padding: '8px 0 0' }}>
                          {pivot.id
                            ? <EditableText fieldKey={`pivot.${pivot.id}.description`} fallback={pivot.displayDescription} label="Vipupisteen kuvaus" />
                            : pivot.displayDescription}
                        </PivotDescription>
                      )}
                      {summariesByPivot[pivot.id] && (summariesByPivot[pivot.id].summary || summariesByPivot[pivot.id].biggestBlocker) && (
                        <div style={{
                          marginTop: 16,
                          padding: '16px 18px',
                          background: '#f5f3ff',
                          border: '1px solid #ddd6fe',
                          borderRadius: 12,
                        }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                            AI-ehdotus · Linssien yhteenveto
                          </div>
                          {summariesByPivot[pivot.id].summary && (
                            <div style={{ fontSize: 14, lineHeight: 1.65, color: '#1f2937', whiteSpace: 'pre-line', marginBottom: 12 }}>
                              <EditableText
                                fieldKey={`pivot.${pivot.id}.summary`}
                                fallback={summariesByPivot[pivot.id].summary}
                                label="Vipupisteen yhteenveto"
                              />
                            </div>
                          )}
                          {summariesByPivot[pivot.id].biggestBlocker && (
                            <div style={{ marginTop: 8, padding: '10px 12px', background: SITRA.puolukkaBg, borderLeft: `3px solid ${SITRA.puolukka}`, borderRadius: 6 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: SITRA.puolukka, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                                Suurin este
                              </div>
                              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                                <EditableText
                                  fieldKey={`pivot.${pivot.id}.biggestBlocker`}
                                  fallback={summariesByPivot[pivot.id].biggestBlocker}
                                  label="Suurin este"
                                />
                              </div>
                            </div>
                          )}
                          {summariesByPivot[pivot.id].biggestOpportunity && (
                            <div style={{ marginTop: 8, padding: '10px 12px', background: '#f0fdf4', borderLeft: '3px solid #00613d', borderRadius: 6 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#00613d', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
                                Suurin mahdollisuus
                              </div>
                              <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                                <EditableText
                                  fieldKey={`pivot.${pivot.id}.biggestOpportunity`}
                                  fallback={summariesByPivot[pivot.id].biggestOpportunity}
                                  label="Suurin mahdollisuus"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {obstacles.length > 0 && (
                        <ItemsSection>
                          <ItemsSectionTitle $color={SITRA.puolukka}>
                            Esteet ({obstacles.length})
                          </ItemsSectionTitle>
                          {obstacles.map(item => (
                            <PivotItemRow key={item.id}>
                              <ItemDot $type="obstacle" />
                              <span>
                                <TranslatedText
                                  fieldKey={`lensItem.${item.id}.title`}
                                  fallback={item.title}
                                  multiline={false}
                                  label="Esteen kuvaus"
                                />
                              </span>
                            </PivotItemRow>
                          ))}
                        </ItemsSection>
                      )}
                      {opportunities.length > 0 && (
                        <ItemsSection>
                          <ItemsSectionTitle $color="#00613d">
                            Mahdollisuudet ({opportunities.length})
                          </ItemsSectionTitle>
                          {opportunities.map(item => (
                            <PivotItemRow key={item.id}>
                              <ItemDot $type="opportunity" />
                              <span>
                                <TranslatedText
                                  fieldKey={`lensItem.${item.id}.title`}
                                  fallback={item.title}
                                  multiline={false}
                                  label="Mahdollisuuden kuvaus"
                                />
                              </span>
                            </PivotItemRow>
                          ))}
                        </ItemsSection>
                      )}
                    </PivotBody>
                  )}
                </PivotCard>
              );
            })}
          </PivotGrid>

              {hasSelection && otherPivots.length > 0 && (
                <>
                  <div
                    onClick={() => setShowAllPivots(!showAllPivots)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '24px 0 12px', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem' }}
                  >
                    {showAllPivots ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showAllPivots ? t('report.common.hide') : t('report.pivots.showAllPivots', { count: aiPivots.length })}
                  </div>
                  {showAllPivots && (
                    <PivotGrid>
                      {otherPivots.map((pivot, i) => {
                        const pivotItems = itemsByPivot[pivot.id] || [];
                        const obstacles = pivotItems.filter(it => it.type === 'obstacle');
                        const opportunities = pivotItems.filter(it => it.type !== 'obstacle');
                        const primaryColor = getScenarioColor((pivot.scenarioNames || [])[0]);
                        return (
                          <PivotCard key={pivot.id} $color={primaryColor}>
                            <PivotHeader>
                              <PivotTitle>
                                {pivot.id
                                  ? <EditableText fieldKey={`pivot.${pivot.id}.title`} fallback={pivot.displayTitle} multiline={false} label="Vipupisteen otsikko" />
                                  : pivot.displayTitle}
                              </PivotTitle>
                              <PivotMeta>
                                {(pivot.scenarioNames || []).map(name => (
                                  <ScenarioPill key={name} $color={getScenarioColor(name)}>{translateScenarioName(name)}</ScenarioPill>
                                ))}
                                {(pivot.memberCount || pivot.size) && (
                                  <MetaBadge>{t('report.pivots.connections', { n: pivot.memberCount || pivot.size })}</MetaBadge>
                                )}
                              </PivotMeta>
                            </PivotHeader>
                          </PivotCard>
                        );
                      })}
                    </PivotGrid>
                  )}
                </>
              )}
            </>);
          })()}
        </>
      )}

      {/* --- Human-created pivot points (day 2) --- */}
      {humanPivots.length > 0 && (
        <>
          <SectionSubtitle style={{ marginTop: aiPivots.length > 0 ? '40px' : '0' }}>
            {t('report.pivots.humanTitle')}
            <SourceTag $manual>{t('report.pivots.smallGroupWork')}</SourceTag>
          </SectionSubtitle>
          <Paragraph>
            {t('report.pivots.humanIntro')}
          </Paragraph>

          <PivotGrid>
            {humanPivots.map(pivot => {
              const isExpanded = expandedPivot === pivot.id;
              return (
                <PivotCard key={pivot.id} $color="#7a3a0c">
                  <PivotHeader onClick={() => pivot.displayDescription && setExpandedPivot(isExpanded ? null : pivot.id)}>
                    <PivotTitle>
                      {pivot.id
                        ? <EditableText fieldKey={`pivot.${pivot.id}.title`} fallback={pivot.displayTitle} multiline={false} label="Vipupisteen otsikko" />
                        : pivot.displayTitle}
                    </PivotTitle>
                    {pivot.displayDescription && (
                      <ToggleIcon>
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </ToggleIcon>
                    )}
                  </PivotHeader>
                  {isExpanded && pivot.displayDescription && (
                    <PivotBody>
                      <PivotDescription style={{ padding: '8px 0 0' }}>
                        {pivot.id
                          ? <EditableText fieldKey={`pivot.${pivot.id}.description`} fallback={pivot.displayDescription} label="Vipupisteen kuvaus" />
                          : pivot.displayDescription}
                      </PivotDescription>
                    </PivotBody>
                  )}
                </PivotCard>
              );
            })}
          </PivotGrid>
        </>
      )}
    </Section>
  );
}
